import { RiskyDisclosureParagraph } from '@/components/common/DisclosureParagraph';
import { FlexRow } from '@/components/common/Flex';
import FinancialEntityRelinkWarning from '@/components/financialEntities/FinancialEntityRelinkWarning';
import { useUserData } from '@/services/user';
import { useMutation, useQuery } from '@apollo/client';
import { assert } from '@common/errors';
import {
    ExternalFinancialEntity,
    feSnapshotInfoSchemaByFEType,
    FinancialEntitySnapshotInfo,
    getLinkedFields,
    getName,
} from '@common/financialEntities';
import { FieldConfig } from '@common/financialEntities/config';
import {
    FinancialEntityDocument,
    FinancialEntityFieldsFragment,
    FinancialEntityType,
    UpdateFinancialEntityDocument,
} from '@common/graphql/generated';
import { isInvalidForm } from '@common/react/accounts';
import { isDisconnectedOrError } from '@common/react/financialEntities';
import { UserInfo } from '@common/react/user';
import { TOASTS } from '@common/toasts';
import { typecheck } from '@common/typebox';
import { typedKeys } from '@common/util/objects';
import { isNumber } from '@common/util/types';
import isEqual from 'lodash.isequal';
import { FC, useEffect, useState } from 'react';
import { EntityViewContent, entityViewInset } from '../common/EntityView';
import ErrorBoundary from '../common/ErrorBoundary';
import FadeInBox from '../common/FadeInBox';
import { FlexCol } from '../common/Flex';
import Form from '../common/Form';
import LoadingWrapper from '../common/LoadingWrapper';
import { useToasts } from '../common/Toasts';
import { ChangedFields } from './ChangesDescription';
import FinancialEntityDetailChartView from './FinancialEntityDetailChartView';
import FinancialEntityDetailControls from './FinancialEntityDetailControls';
import FinancialEntityDetailHeader from './FinancialEntityDetailHeader';
import FinancialEntityDetailPanels from './FinancialEntityDetailPanels';
import { getConfig } from './helpers';

export interface FinancialEntityDetailViewProps {
    id: string;
    onClose: VoidFunction;
}

const FinancialEntityDetailView: FC<FinancialEntityDetailViewProps> = ({
    id,
    onClose,
}) => {
    const { userInfo } = useUserData();
    const { data, loading, error } = useQuery(FinancialEntityDocument, {
        fetchPolicy: 'cache-and-network',
        variables: { id },
    });
    if (!userInfo) return null;
    return (
        <LoadingWrapper error={error} isLoading={!data || loading}>
            <ErrorBoundary>
                {data?.financialEntity ? (
                    <FinancialEntityDetailInnerView
                        financialEntity={
                            // TODO: avoid type assertion
                            data.financialEntity as ExternalFinancialEntity
                        }
                        userInfo={userInfo}
                        onClose={onClose}
                    />
                ) : (
                    <></>
                )}
            </ErrorBoundary>
        </LoadingWrapper>
    );
};

export interface FinancialEntityDetailInnerViewProps {
    financialEntity: ExternalFinancialEntity;
    userInfo: UserInfo;
    onClose: VoidFunction;
}

const FinancialEntityDetailInnerView: FC<
    FinancialEntityDetailInnerViewProps
> = ({ financialEntity: fe, userInfo, onClose }) => {
    const isMyFE = fe.isMine;
    const config = getConfig(fe.type);
    const detailViewConfig = config.detailView;
    const [lastUpdated, setLastUpdated] = useState(fe.updatedAt);
    const showRelinkWarning = fe.isMine && isDisconnectedOrError(fe);
    const { showSuccessToast } = useToasts();

    // Construct mutation hook
    const [updateFinancialEntity, { loading }] = useMutation(
        UpdateFinancialEntityDocument
    );

    const allFields = [
        detailViewConfig.primaryField,
        ...detailViewConfig.expectedFields,
        ...detailViewConfig.otherFields,
    ];
    const [initialForm, setInitialForm] = useState(
        getInitialState(fe, allFields)
    );
    const [form, setForm] = useState(initialForm);
    const changeField = (name: string, value: any) => {
        // Block changes on partner's FE - this should never happen!
        if (!isMyFE) return;

        setForm({
            ...form,
            [name]: value,
        });
    };

    const linkedFields = getLinkedFields(fe, allFields);

    const changedFields: ChangedFields = [];
    Object.keys(initialForm).forEach((k) => {
        if (!isEqual(form[k], initialForm[k])) {
            const field = allFields.find((f) => f.name === k);
            assert(field);
            changedFields.push({
                field,
                oldVal: initialForm[k],
                newVal: form[k],
            });
        }
    });

    // Track if financial entity is externally updated (eg by Plaid);
    // if it is, reset internal form state.
    useEffect(() => {
        if (fe.updatedAt > lastUpdated) {
            const newInitialForm = getInitialState(fe, allFields);
            setInitialForm(newInitialForm);
            setForm(newInitialForm);
            setLastUpdated(fe.updatedAt);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fe.updatedAt]);

    // Simple required/optional validation
    const invalid = isInvalidForm(form, allFields);

    // Construct submit function
    const submit = async () => {
        if (invalid) return;

        const formData = { ...form };

        // Sanitize form values
        typedKeys(form).forEach((k) => {
            // TODO: Update boolean-relevant form inputs to actually export true/false
            if (formData[k] === 'true') formData[k] = true;
            if (formData[k] === 'false') formData[k] = false;
        });

        // Build form data objects for base table and snapshot table
        const baseFormData: Record<string, unknown> = {};
        const snapshotFormData: Record<string, unknown> = {};
        const { snapshotFieldNames } = getConfig(fe.type);
        changedFields.forEach(({ field }) => {
            if (snapshotFieldNames.includes(field.name)) {
                snapshotFormData[field.name] = formData[field.name];
            } else {
                baseFormData[field.name] = formData[field.name];
            }
        });

        const snapshot = maybeGetSnapshot({
            feType: fe.type,
            snapshotFormData,
        });

        const variables = {
            id: fe.id,
            patch: {
                info: { ...fe.info, ...baseFormData },
                ...(snapshot
                    ? { balance: snapshot.balance, snapshotInfo: snapshot.info }
                    : {}),
            },
        };

        const res = await updateFinancialEntity({
            variables,
        });

        if (res.data?.updateFinancialEntity?.financialEntity) {
            const updatedFE = res.data.updateFinancialEntity.financialEntity;
            // Reset state
            const newInitialForm = getInitialState(updatedFE, allFields);
            setInitialForm(newInitialForm);
            setForm(newInitialForm);
            showSuccessToast(
                TOASTS.updateFinancialEntitySuccess({ feName: getName(fe) })
            );
        }
    };

    return (
        <Form
            key={fe.id}
            h="full"
            disabled={false}
            onSubmit={submit}
            data-cy="financial-entity-detail-view"
        >
            <FadeInBox display="block">
                <FinancialEntityDetailHeader
                    financialEntity={fe}
                    changedFields={changedFields}
                    isSaveDisabled={invalid}
                    isSaveLoading={loading}
                    onSave={submit}
                    onCancelChanges={() => setForm(initialForm)}
                    flexGrow={0}
                />
                <EntityViewContent pt={showRelinkWarning ? 6 : 12}>
                    {showRelinkWarning && (
                        <FinancialEntityRelinkWarning
                            account={fe}
                            includeFinancialEntityWithMask={false}
                            mx={10}
                            mb={6}
                        />
                    )}
                    {isMyFE && (
                        <FlexRow
                            justify="flex-end"
                            align="center"
                            px={entityViewInset}
                        >
                            <FinancialEntityDetailControls
                                fe={fe}
                                closeDetailView={onClose}
                            />
                        </FlexRow>
                    )}
                    <FinancialEntityDetailChartView
                        fe={fe}
                        form={form}
                        field={detailViewConfig.primaryField}
                        linkedFields={linkedFields}
                        onFieldChange={changeField}
                        userInfo={userInfo}
                        px={12}
                        mb={16}
                    />
                    <FlexCol basis={0} grow={1} pb={8}>
                        <FinancialEntityDetailPanels
                            financialEntity={fe}
                            form={form}
                            expectedFields={detailViewConfig.expectedFields}
                            infoFields={detailViewConfig.otherFields}
                            linkedFields={linkedFields}
                            onFieldChange={changeField}
                        />
                    </FlexCol>
                    <RiskyDisclosureParagraph px={16} py={8} />
                </EntityViewContent>
            </FadeInBox>
        </Form>
    );
};

const maybeGetSnapshot = ({
    feType,
    snapshotFormData,
}: {
    feType: FinancialEntityType;
    snapshotFormData: Record<string, unknown>;
}): { balance: number; info: FinancialEntitySnapshotInfo } | undefined => {
    if (Object.keys(snapshotFormData).length === 0) return;

    const { balance, ...rest } = snapshotFormData;

    assert(isNumber(balance));

    const snapshotInfo = { ...rest, feType };
    const snapshotInfoSchema = feSnapshotInfoSchemaByFEType[feType];
    typecheck(snapshotInfoSchema, snapshotInfo, (errors) => {
        console.warn({ snapshotInfo, errors });
        throw new Error('maybeGetSnapshot failed typecheck');
    });

    return { balance, info: snapshotInfo };
};

const getInitialState = (
    fe: FinancialEntityFieldsFragment,
    fields: FieldConfig[]
): Record<string, any> => {
    return fields.reduce(
        (initialForm, field) => {
            let value;
            if (field.name in fe.info) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                value = fe.info[field.name];
            } else if (fe.latestSnapshot && field.name in fe.latestSnapshot) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                value = fe.latestSnapshot[field.name];
            } else {
                value = field.defaultValue;
            }
            if (value === true) value = 'true';
            if (value === false) value = 'false';

            initialForm[field.name] = value;
            return initialForm;
        },
        {} as Record<string, any>
    );
};

export default FinancialEntityDetailView;
